import type { ProviderFile } from "fileParsing/types";
import { v4 } from "uuid";
import {
  type ArrayType,
  type DataPointTemplate,
  EMPTY_SCHEMA,
  genericPath,
  type ObjectType,
  type Path,
  type TypeSchema,
  type UnionType,
  Visibility,
  WILDCARD,
  type WranglerItem,
} from "./types";

export const extractTemplatesFn = (wranglerItem: WranglerItem, files: ProviderFile[]) => {
  // Get List of Paths to DataArrays from Schema
  const paths = findDataPaths(wranglerItem.schema);
  // Transform list to DPTemplates
  const templates = paths.map((path) =>
    createDataPointTemplate(
      path,
      extractFromTypeSchema(wranglerItem.schema, path) ?? EMPTY_SCHEMA,
      Visibility.visible,
      files,
    ),
  );
  return {
    ...wranglerItem,
    templates,
  };
};

export const findDataPaths = (schema: UnionType): Path[] => {
  const findInUnionType = (schema: UnionType, path: Path): Path[] => {
    return schema.reduce<Path[]>((flat, type) => [...flat, ...findInTypeSchema(type, path)], []);
  };

  const findInTypeSchema = (schema: TypeSchema, path: Path): Path[] => {
    if (schema.type === "array") {
      const children = schema.anyOf.reduce<Path[]>(
        (flat, type) => [...flat, ...findInTypeSchema(type, [...path, null])],
        [],
      );

      // DataPoints must be object in arrays
      if (schema.anyOf.some((t) => t.type === "object")) {
        return [path, ...children];
      }
      return children;
    } else if (schema.type === "object") {
      return Object.entries(schema.properties).reduce<Path[]>(
        (flat, [k, v]) => [...flat, ...findInUnionType(v, [...path, k])],
        [],
      );
    }

    return [];
  };

  return findInUnionType(schema, []);
};

export const extractFromTypeSchema = (schema: UnionType, path: Path): UnionType | undefined => {
  if (path.length === 0) {
    return schema;
  }

  const arr = schema.find(({ type }) => type === "array");
  if (path[0] === WILDCARD && arr !== undefined) {
    return extractFromTypeSchema((arr as ArrayType).anyOf, path.slice(1));
  }

  const obj = schema.find(({ type }) => type === "object");
  if (path[0] !== WILDCARD && obj !== undefined) {
    return extractFromTypeSchema((obj as ObjectType).properties[path[0]], path.slice(1));
  }

  return undefined;
};

export const countDataPoints = (file: ProviderFile, path: Path): number =>
  countAtPath(file.parserData?.structure, path.map(genericPath));

const countAtPath = (tree: unknown, path: Path): number => {
  if (path.length === 0) return Array.isArray(tree) ? tree.length : 1;
  if (Array.isArray(tree)) {
    return path[0] === WILDCARD
      ? tree.reduce<number>((n, t) => n + countAtPath(t, path.slice(1)), 0)
      : typeof path[0] === "number"
        ? countAtPath(tree[path[0]], path.slice(1))
        : 0;
  }
  if (tree !== null && typeof tree === "object") {
    const obj = tree as Record<string, unknown>;
    if (path[0] === WILDCARD) return Object.values(obj).reduce<number>((n, v) => n + countAtPath(v, path.slice(1)), 0);
    const key = typeof path[0] === "number" ? Object.keys(obj)[path[0]] : path[0];
    return typeof key === "string" && key in obj ? countAtPath(obj[key], path.slice(1)) : 0;
  }
  return 0;
};

export const createDataPointTemplate = (
  path: Path,
  dataPointSchema: UnionType,
  visibility: Visibility,
  files: ProviderFile[],
): DataPointTemplate => ({
  path,
  schema: dataPointSchema,
  category: undefined,
  subcategory: [{ id: v4(), type: "text", value: "" }],
  visibility,
  content: [{ id: v4(), type: "text", value: "" }],
  selectedTimestamps: [],
  selectedUris: [],
  selectedContacts: [],
  selectedLatitudes: [],
  selectedLongitudes: [],
  dataPointsCount: files.reduce((sum, file) => sum + countDataPoints(file, path), 0),
  updated: false,
});

export function extractDate(tree: unknown, path: Path): (string | number | unknown)[] {
  if (path.length === 0) {
    return [tree];
  } else if (Array.isArray(tree)) {
    if (path[0] === WILDCARD) {
      return (tree as unknown[])
        .map((t: unknown) => extractDate(t, path.slice(1)))
        .slice(0, 1000) // fix some performance issues with very large arrays
        .reduce<(string | number | unknown)[]>((result, arr) => result.concat(arr), []);
    } else if (typeof path[0] === "number") {
      return extractDate((tree as unknown[])[path[0]], path.slice(1));
    }
  } else if (tree !== null && typeof tree === "object") {
    const obj = tree as Record<string, unknown>;
    if (path[0] === WILDCARD) {
      return Object.values(obj)
        .map((v) => extractDate(v, path.slice(1)))
        .reduce((result, arr) => result.concat(arr), []);
    } else {
      let key: string;

      if (typeof path[0] === "string") key = path[0];
      // Path may be exact-path. If object has been parsed by WILDCARD from
      // wrangler definition, path[0] is the index of the item in the object
      // rather than the actual key.
      else if (typeof path[0] === "number") key = Object.keys(obj)[path[0]];

      if (key !== undefined && key in obj) {
        const pVal = obj[key];
        return extractDate(pVal, path.slice(1));
      }
    }
  }
  return [];
}

export function extractRandomSample(tree: unknown, path: Path): string | number | unknown {
  if (path.length === 0) {
    return tree;
  } else if (Array.isArray(tree)) {
    if (path[0] === WILDCARD) {
      const arr = tree as unknown[];
      const offset = Math.floor(arr.length * Math.random());

      for (let i = 0; i < arr.length; i++) {
        const ix = (i + offset) % arr.length;
        const item = extractRandomSample(arr[ix], path.slice(1));
        if (item !== undefined) return item;
      }
    } else if (typeof path[0] === "number") {
      return extractRandomSample((tree as unknown[])[path[0]], path.slice(1));
    }
  } else if (path[0] !== WILDCARD && tree !== null && typeof tree === "object") {
    const key = path[0];
    const obj = tree as Record<string, unknown>;
    if (key in obj) {
      const pVal = obj[key];
      return extractRandomSample(pVal, path.slice(1));
    }
  }
  return undefined;
}

function determineArrays(schema: UnionType): Path[] {
  return schema.reduce<Path[]>((arr, el) => {
    const paths = determineArraysInTypeSchema(el);
    if (paths !== undefined) {
      return [...arr, ...paths];
    }
    return arr;
  }, []);
}

function determineArraysInTypeSchema(schema: TypeSchema): Path[] | undefined {
  if (schema.type === "array") {
    return schema.anyOf.reduce<Path[]>(
      (arr, el) => {
        const paths = determineArraysInTypeSchema(el);
        if (paths !== undefined) {
          return [...arr, ...paths.map((path) => [null, ...path])];
        }
        return arr;
      },
      [[]],
    );
  } else if (schema.type === "object") {
    return Object.entries(schema.properties).reduce<Path[]>((arr, [k, v]) => {
      const paths = determineArrays(v);
      if (paths !== undefined) {
        return [...arr, ...paths.map((path) => [k, ...path])];
      }
      return arr;
    }, []);
  } else if (schema.type === "string") {
    return [];
  }

  return undefined;
}
